import { useEffect, useState } from 'react';
import { useReturnStore } from '@src/stores/returnStore';
import { IErrorReturnService } from '@src/models/errors';
import {
  handleAprissCall,
  handleMultiReturnCreateCall,
  handleSORISCreateCall,
  handleVersionIDCall,
  getIdentifier,
} from '@src/utils/api-helpers';
import {
  AprissFinalizeRequest,
  CreateReturnOrderData,
  ICreateReturnRequest,
} from '@src/models/api';
import {
  buildAprissFinalizeRequest,
  buildSsReturnCreateRequest,
  buildFinalizeSORISRequest,
} from '@src/utils';
import { useNewFlowStore } from '@src/stores/newFlowStore';
import {
  RETURN_METHOD,
  RETURN_METHOD_TO_SHIPMENT_MAP,
  ReturnMethodType,
} from '@src/constants/general';
import { OrderDetailsSessionDataV2 } from '@src/models/returns';
import { useSummaryStore } from '@src/stores/summaryStore';
import { useNavigateToReturnSummary } from './useNavigateToReturnSummary';

export interface CallInformation {
  type: ReturnMethodType;
  success: boolean;
  data: CreateReturnOrderData;
}

export interface APICallState {
  currentStep: number;
  totalSteps: number;
  stopped: boolean;
  completedCalls: CallInformation[];
  callOrder: ReturnMethodType[];
  failedCallAmount: number;
  hasDuplicateError: boolean;
  initialCall: boolean;
}

const useMultiCallCreateReturn = () => {
  const {
    orderDetailsV2,
    isMilitaryExchangeGuest,
    policyValidatorResponse,
    returnShipments,
    selectedItems,
    fraudDecisions,
  } = useReturnStore((state) => state);
  const methods = useNewFlowStore((state) => state.returnMethods);
  const refundValues = useSummaryStore((state) => state.refundValues);

  const [APICallState, setAPICallState] = useState<APICallState>({
    currentStep: 0,
    totalSteps: 0,
    completedCalls: [],
    callOrder: [],
    stopped: true,
    failedCallAmount: 0,
    hasDuplicateError: false,
    initialCall: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<IErrorReturnService | null>(null);
  const { currentStep, totalSteps, callOrder } = APICallState;

  // Hook to handle navigation to return summary
  const navigateToReturnSummary = useNavigateToReturnSummary();

  const getNewVersionId = async () => {
    const { versionId, error } = await handleVersionIDCall(
      orderDetailsV2?.orderNumber || '',
      orderDetailsV2?.emailId || '',
      isMilitaryExchangeGuest,
      orderDetailsV2?.net,
    );
    if (error) {
      setAPICallState((prev) => ({ ...prev, stopped: true }));
      setApiError(error);
      return null;
    }
    return versionId || null;
  };

  const getRequestForMethod = (
    method: ReturnMethodType,
    orderVersion: string,
  ): ICreateReturnRequest | null => {
    if (!orderDetailsV2 || !policyValidatorResponse) return null;
    // Build and return the request object based on the method type
    const { deliveryDictionary } = orderDetailsV2;
    const { et = '' } = policyValidatorResponse || {};
    return buildSsReturnCreateRequest({
      orderDetailsV2,
      isGuest: isMilitaryExchangeGuest,
      policyValidatorET: et,
      deliveryDictionary,
      returnShipments,
      selectedItems,
      shipmentType: RETURN_METHOD_TO_SHIPMENT_MAP[method],
      orderVersion,
    });
  };

  // For SORIS, we instead send SorisId and currentTime for Apriss Finalize.
  // For UPS/LORP, we send orderId of the RETURN and return creation time.
  const callAprissFinalize = (
    returnOrderId: string,
    orderDateForReturn: string,
    method: ReturnMethodType,
  ) => {
    if (!orderDetailsV2) return;

    const shipmentType = RETURN_METHOD_TO_SHIPMENT_MAP[method];
    const request: AprissFinalizeRequest = buildAprissFinalizeRequest({
      orderDetailsV2,
      returnOrderId,
      orderDateForReturn,
      policyValidatorET: policyValidatorResponse?.et || '',
      shipmentType,
      identifier: getIdentifier(fraudDecisions, shipmentType) || '0',
      isGuest: isMilitaryExchangeGuest,
    });

    handleAprissCall(request);
  };

  const makeCreateReturnCalls = async () => {
    setAPICallState((prev) => ({
      ...prev,
      stopped: false,
      failedCallAmount: 0,
    }));
    setApiError(null);
  };

  const handleCallError = (currentCall: ReturnMethodType) => {
    setAPICallState((prev) => ({
      ...prev,
      callOrder: [
        ...prev.callOrder.filter((method) => method !== currentCall),
        currentCall,
      ],
      failedCallAmount: prev.failedCallAmount + 1,
    }));
  };

  const handleCallSuccess = (newCallInfo: CallInformation) => {
    setAPICallState((prev) => ({
      ...prev,
      currentStep: prev.currentStep + 1,
      completedCalls: [...prev.completedCalls, newCallInfo],
    }));
  };

  const handleSORISCall = async (
    currentCall: ReturnMethodType,
    orderDetails: OrderDetailsSessionDataV2,
  ) => {
    let orderVersion = orderDetails.versionId || '';

    if (!APICallState.initialCall) {
      const newVersionId = await getNewVersionId();
      if (!newVersionId) return;
      orderVersion = newVersionId;
    } else setAPICallState((prev) => ({ ...prev, initialCall: false }));

    // Pass all selectedItems so buildFinalizeSORISRequest can detect mixed shipment scenario
    // and recalculate refund for SORIS items only if needed
    const sorisRequest = await buildFinalizeSORISRequest(
      orderDetails,
      selectedItems,
      isMilitaryExchangeGuest,
      refundValues?.total || '',
      orderVersion,
    );

    const { data, error } = await handleSORISCreateCall(sorisRequest);
    if (error) {
      handleCallError(currentCall);
      setApiError(error);
      return;
    }
    if (data) {
      // Map SORIS response to CreateReturnOrderData format for consistency
      const newCallInfo: CallInformation = {
        type: currentCall,
        success: true,
        data: {
          orderId: data.orderId,
          returnOrderId: data.orderId,
          returnByDate: data.returnByDate,
          orderDateForReturn: '',
          returnTrackingNumbers: [],
          sorisId: data.sorisId,
          sorisQRCode: data.sorisQRCode,
        },
      };
      callAprissFinalize(data.sorisId, new Date().toISOString(), currentCall);
      handleCallSuccess(newCallInfo);
    }
  };

  const handleUPSLORPCall = async (
    currentCall: ReturnMethodType,
    orderDetails: OrderDetailsSessionDataV2,
  ) => {
    let orderVersion = orderDetails.versionId || '';

    if (!APICallState.initialCall) {
      const newVersionId = await getNewVersionId();
      if (!newVersionId) return;
      orderVersion = newVersionId;
    } else setAPICallState((prev) => ({ ...prev, initialCall: false }));

    const request = getRequestForMethod(currentCall, orderVersion);
    if (!request) return;

    const { data, error } = await handleMultiReturnCreateCall(request);
    if (error) {
      const isDuplicateError =
        error.code === 'COM_RETURN_RECORD_ALREADY_EXISTS_ERROR';
      if (isDuplicateError) {
        // If duplicate error, we can consider this call successful without data
        // Skip Apriss Finalize call since we don't have a returnOrderId
        const newCallInfo: CallInformation = {
          type: currentCall,
          success: true,
          data: {} as CreateReturnOrderData,
        };
        setAPICallState((prev) => ({
          ...prev,
          currentStep: prev.currentStep + 1,
          completedCalls: [...prev.completedCalls, newCallInfo],
          hasDuplicateError: true,
        }));
        return;
      }
      // generic error, add +1 to failedCallAmount
      handleCallError(currentCall);
      setApiError(error);
      return;
    }
    if (data) {
      const newCallInfo: CallInformation = {
        type: currentCall,
        success: true,
        data,
      };
      callAprissFinalize(
        data.returnOrderId,
        data.orderDateForReturn,
        currentCall,
      );
      handleCallSuccess(newCallInfo);
    }
  };

  const makeNextCall = async () => {
    if (currentStep >= totalSteps || isLoading) return;
    if (!orderDetailsV2) {
      setApiError({
        description: 'Missing order details',
        code: '',
        retryable: true,
      });
      setAPICallState((prev) => ({ ...prev, stopped: true }));
      return;
    }

    const currentCall = callOrder[currentStep];

    if (currentCall === RETURN_METHOD.SORIS) {
      await handleSORISCall(currentCall, orderDetailsV2);
    } else {
      await handleUPSLORPCall(currentCall, orderDetailsV2);
    }
  };

  const callAPI = async () => {
    setIsLoading(true);
    await makeNextCall();
    setIsLoading(false);
  };

  useEffect(() => {
    // if any call failed, we need to check if we reached the limit
    if (
      !APICallState.stopped &&
      APICallState.failedCallAmount > 0 &&
      currentStep >= totalSteps - APICallState.failedCallAmount
    ) {
      // stop the call process
      setAPICallState((prev) => ({ ...prev, stopped: true }));
      return;
    }
    // finished, do nothing
    if (currentStep >= totalSteps && totalSteps !== 0) return;
    // only proceed if not stopped and not already loading, and still missing steps
    if (currentStep < totalSteps && !APICallState.stopped && !isLoading) {
      callAPI();
    }
  }, [APICallState, isLoading]);

  // check if all calls completed successfully
  useEffect(() => {
    const handleSuccessfulCompletion = async () => {
      if (!orderDetailsV2) return;
      // All calls finished successfully - recalculate refund and navigate to summary
      if (
        APICallState.completedCalls.length > 0 &&
        APICallState.completedCalls.length === APICallState.totalSteps
      ) {
        navigateToReturnSummary(
          APICallState.completedCalls,
          APICallState.hasDuplicateError,
        );
      }
    };

    handleSuccessfulCompletion();
  }, [
    APICallState.completedCalls,
    navigateToReturnSummary,
    orderDetailsV2,
    selectedItems,
  ]);

  // Determine the order of calls based on available methods
  useEffect(() => {
    const order: ReturnMethodType[] = [];
    if (methods.includes(RETURN_METHOD.UPS_DROP_OFF)) {
      order.push(RETURN_METHOD.UPS_DROP_OFF);
    }
    if (methods.includes(RETURN_METHOD.LORP)) {
      order.push(RETURN_METHOD.LORP);
    }
    if (methods.includes(RETURN_METHOD.SORIS)) {
      order.push(RETURN_METHOD.SORIS);
    }
    setAPICallState((prev) => ({
      ...prev,
      callOrder: order,
      totalSteps: order.length,
    }));
  }, [methods, returnShipments]);

  return {
    APICallState,
    makeCreateReturnCalls,
    apiError: APICallState.stopped ? apiError : null,
    isLoading,
  };
};

export default useMultiCallCreateReturn;
